import React, { type ReactNode, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useButton } from 'react-aria';
import { InView } from 'react-intersection-observer';
import { Coin } from '@hungryroot/icons';
import { Icon, type IconVariant } from '@hungryroot/icons';
import {
  ProductCardImage,
  IngredientsImageGrid,
  Variant as IngredientsImageGridVariant,
  BinaryRatingRecipeScore,
  Badge,
  BadgeVariant,
  MAX_BADGES,
  MAX_BADGES_FOR_YOU,
} from '@hungryroot/pho';
import { Paragraph, Heading } from '@hungryroot/ramen';
import {
  BinaryRatings,
  trackProductSaved,
  getLikeDislikeRatingFromPairing,
  ApiV1LikeDislikeEnum as LikeDislike,
  LikedDislikedIndicator,
} from '@hungryroot/ratings';
import {
  type PairingV1 as Pairing,
  type ProductV1 as Product,
} from '@hungryroot/rest';
import AddRemoveButton from '../AddRemoveButton/AddRemoveButton';

import styles from './ProductCard.module.css';

type PairingPropType = Pick<
  Pairing,
  'id' | 'recipe_score' | 'liked' | 'num_ratings'
>;

export enum StyleVariant {
  Small = 'small',
  Medium = 'medium',
  SmallToLarge = 'small-to-large',
  Fluid = 'fluid',
}

type BinaryRatingsProps = {
  pairingData: Pairing;
  orderId: number;
  customerId: number;
  pathForTracking?: string;
  onRatingSheetClosed?: () => void;
};

type ShopActionProps = {
  productData: Pairing | Product;
  addProduct?: (recipe: Pairing | Product) => void;
  removeProduct?: (recipe: Pairing | Product) => void;
  isNotAddable: () => boolean;
  cartCount: number;
  onAddToCart?: () => void;
};

type Props = {
  testid?: string;
  styleVariant: StyleVariant;
  hideAddRemoveButton?: boolean;
  featuredImage: {
    src: string | null | undefined;
    alt: string;
  };
  ingredients?: Pairing['ingredients'];
  title: string;
  label?: {
    icon?: IconVariant;
    text: string;
  };
  credits?: number;
  details?: Array<{
    icon: IconVariant;
    text: string;
  }>;
  badges?: Array<string>;
  favoriteAction?: ReactNode;
  binaryRatings?: BinaryRatingsProps;
  binaryRatingRecipeScore?: PairingPropType;
  shopAction?: ShopActionProps;
  markedAsNever?: boolean;
  onClick?: () => void;
  className?: string;
  topLeftLabel?: string;
  overlayContent?: ReactNode;
  servingSize?: ReactNode;
  priorityBadge?: ReactNode;
  onViewItem?: () => void;
  rootMargin?: string;
  isRecipe?: boolean;
};

export function ProductCard(props: Props) {
  const {
    testid,
    styleVariant,
    featuredImage,
    hideAddRemoveButton,
    ingredients,
    title,
    label,
    credits,
    details,
    favoriteAction,
    binaryRatings,
    binaryRatingRecipeScore,
    shopAction,
    markedAsNever,
    onClick,
    className,
    topLeftLabel,
    badges,
    overlayContent,
    servingSize,
    priorityBadge,
    onViewItem,
    rootMargin,
    isRecipe,
  } = props;
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasBeenViewed = useRef(false);
  const isInView = useRef(false);

  const handleScrollStop = useCallback(() => {
    // debounce the scroll event for only when the user is not scrolling
    // this indicates that the user is more intentionally "viewing" the item
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (isInView.current && !hasBeenViewed.current) {
        onViewItem?.();
        hasBeenViewed.current = true;
      }
    }, 300);
  }, [onViewItem]);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollStop, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScrollStop);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleScrollStop]);

  // if the item is initially in view, fire the callback
  useEffect(() => {
    if (isInView.current && !hasBeenViewed.current) {
      onViewItem?.();
      hasBeenViewed.current = true;
    }
  }, [isInView.current, hasBeenViewed.current]);

  const labelIcon = label && label.icon ? <Icon variant={label.icon} /> : null;
  const formattedDetails = details
    ? details.map((detail, i) => {
        return (
          <div className={styles.detail} key={detail.text}>
            <Icon variant={detail.icon} />
            <Paragraph testid={`${testid}-detail-${i}`} variant="caption">
              {detail.text}
            </Paragraph>
          </div>
        );
      })
    : undefined;

  const ref = useRef(null);
  const favoriteActionRef = useRef<HTMLDivElement>(null);

  const { buttonProps } = useButton(
    {
      onPress: () => onClick && onClick(),
      elementType: 'div',
    },
    ref
  );

  const { buttonProps: favoriteActionButtonProps } = useButton(
    {
      onPress: (e) => e.continuePropagation(),
      elementType: 'div',
      excludeFromTabOrder: true,
    },
    favoriteActionRef
  );

  const renderBinaryRatings = (binaryRatings: BinaryRatingsProps) => {
    const {
      pairingData,
      orderId,
      onRatingSheetClosed,
      customerId,
      pathForTracking,
    } = binaryRatings;
    return (
      <BinaryRatings
        rating={getLikeDislikeRatingFromPairing(pairingData)}
        onRatingSheetClosed={() => {
          if (typeof onRatingSheetClosed === 'function') {
            onRatingSheetClosed();
          }
        }}
        onRatingChange={(rating) => {
          if (customerId === undefined || pathForTracking === undefined) {
            return;
          }
          trackProductSaved({
            pairingId: pairingData.id,
            pairingName: pairingData.name,
            category: 'pairings',
            location: 'card',
            path: pathForTracking,
            likedDisliked: rating,
          });
        }}
        orderId={orderId}
        pairing={pairingData}
        testid={`product-card-pairing-${pairingData?.id}`}
        className={styles['card-binary-rating']}
      />
    );
  };

  const renderShopIcon = (shopAction: ShopActionProps) => {
    if (!shopAction || !shopAction.productData || hideAddRemoveButton)
      return null;

    return (
      <AddRemoveButton
        testid={`${shopAction.productData.id}-add-remove-button`}
        addProduct={shopAction.addProduct}
        removeProduct={shopAction.removeProduct}
        productData={shopAction.productData}
        isNotAddable={shopAction.isNotAddable}
        cartCount={shopAction.cartCount}
        onAddToCart={shopAction.onAddToCart}
      />
    );
  };

  const renderRating = (binaryRatingRecipeScore: PairingPropType) => {
    const rating = getLikeDislikeRatingFromPairing(binaryRatingRecipeScore);

    if (rating === LikeDislike.Liked || rating === LikeDislike.Disliked) {
      return (
        <LikedDislikedIndicator
          rating={rating}
          testid="liked-disliked-indicator"
        />
      );
    }
    return (
      <div className={styles['product-card-recipe-score']}>
        <BinaryRatingRecipeScore
          numberOfRatings={binaryRatingRecipeScore.num_ratings}
          recipeScore={
            binaryRatingRecipeScore.recipe_score
              ? binaryRatingRecipeScore.recipe_score
              : null
          }
          testid={`binary-rating-recipe-score-${binaryRatingRecipeScore?.id}`}
        />
        {servingSize}
      </div>
    );
  };

  return (
    <InView
      {...buttonProps}
      ref={ref}
      aria-haspopup="dialog"
      data-testid={testid}
      className={clsx(styles[`card-${styleVariant}`], className, {
        [styles['product-card']]: !isRecipe,
      })}
      as="article"
      key={title}
      threshold={0.5}
      triggerOnce={false}
      rootMargin={rootMargin}
      onChange={(inView) => {
        // we need to manage this manually because:
        // 1. we only want to fire the callback when the user is not scrolling
        // 2. we want to debounce the scroll event for when the user has stopped scrolling
        isInView.current = inView;

        if (!inView && scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }
      }}
    >
      {topLeftLabel && (
        <div className={styles['top-left-badge-container']}>
          <Badge
            className={styles['top-left-badge']}
            testid={`${testid}-new-badge`}
            variant={BadgeVariant.New}
          >
            <Paragraph variant="caption">{topLeftLabel}</Paragraph>
          </Badge>
        </div>
      )}
      <div
        className={
          styleVariant === StyleVariant.Fluid
            ? styles['image-container-fluid']
            : styles['image-container']
        }
      >
        {overlayContent && (
          <div data-testid={`${testid}-overlay`} className={styles.overlay}>
            {overlayContent}
          </div>
        )}
        {featuredImage.src ? (
          <ProductCardImage
            testid={testid}
            src={featuredImage.src}
            alt={featuredImage.alt}
            className={
              styleVariant === StyleVariant.Fluid
                ? styles['image-fluid']
                : styles.image
            }
          />
        ) : null}
        {markedAsNever ? (
          <div
            data-testid={`${testid}-marked-as-never`}
            className={styles.nevered}
          >
            <Paragraph>marked as never</Paragraph>
          </div>
        ) : null}
        {!featuredImage.src && ingredients && ingredients.length ? (
          <IngredientsImageGrid
            testid={testid}
            ingredients={ingredients}
            variant={IngredientsImageGridVariant.Secondary}
          />
        ) : null}
        {favoriteAction && (
          <div
            {...favoriteActionButtonProps}
            ref={favoriteActionRef}
            className={styles['product-card-bookmark']}
          >
            {favoriteAction}
          </div>
        )}
        {binaryRatings !== undefined && renderBinaryRatings(binaryRatings)}
        {shopAction !== undefined && renderShopIcon(shopAction)}
      </div>
      <div className={styles.body}>
        {binaryRatingRecipeScore !== undefined &&
          renderRating(binaryRatingRecipeScore)}
        {label ? (
          <div className={styles.label}>
            {labelIcon}
            <Paragraph testid={`${testid}-label`} variant="caption">
              {label.text}
            </Paragraph>
          </div>
        ) : null}
        <Paragraph testid={`${testid}-title`} className={styles.title}>
          {title}
        </Paragraph>
        {credits ? (
          <div className={styles.credits}>
            <Coin />
            <Heading testid={`${testid}-credits`} variant="H6">
              {credits}
            </Heading>
          </div>
        ) : null}
        {details ? (
          <div className={styles.details}>{formattedDetails}</div>
        ) : null}

        {badges && badges?.length > 0 && (
          <div className={styles.badges}>
            {priorityBadge}
            {badges
              .slice(0, priorityBadge ? MAX_BADGES_FOR_YOU : MAX_BADGES)
              .map((badge, i) => (
                <Badge
                  key={badge}
                  testid={`${testid}-badge-${i}`}
                  variant={BadgeVariant.Misc}
                >
                  <Paragraph variant="caption">{badge}</Paragraph>
                </Badge>
              ))}
          </div>
        )}
      </div>
    </InView>
  );
}
